import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useListReceipts, useMyStatements, getMyStatementsQueryKey } from "@workspace/api-client-react";
import type { Receipt, MyStatement, MyStatementEntry } from "@workspace/api-client-react";
import { useReceiptUpload, useReparseAllReceipts, useDeleteReceipt, useDeleteAllReceipts } from "@/hooks/use-uploads";
import { useAuth } from "@/hooks/use-auth";
import { hasGroupAdmin } from "@/lib/pagePermissions";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDropzone } from "@/components/file-dropzone";
import { formatCurrency } from "@/lib/utils";
import {
  Receipt as ReceiptIcon,
  Plus,
  Calendar,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronRight,
  Search,
  X,
  CheckCircle2,
  ShieldCheck,
  FileText,
  Upload,
  AlertCircle,
  CheckCheck,
} from "lucide-react";
import { format } from "date-fns";

type SortMode = "month" | "date_desc" | "date_asc" | "amount_desc" | "amount_asc" | "vendor_asc" | "uploaded_desc";

function parseReceiptDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const ddmmyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const d = new Date(parseInt(ddmmyyyy[3]), parseInt(ddmmyyyy[2]) - 1, parseInt(ddmmyyyy[1]));
    if (!isNaN(d.getTime())) return d;
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

function parseReceiptMonth(dateStr: string | null | undefined): { key: string; label: string } {
  const d = parseReceiptDate(dateStr);
  if (!d) return { key: "zz_unknown", label: "Date Unknown" };
  return { key: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") };
}

function tryParseDate(dateStr: string): string {
  const d = parseReceiptDate(dateStr);
  return d ? format(d, "MMM d, yyyy") : dateStr;
}

// ─────────────────────────────────────────────────────────────────────────────
// Non-admin: statement entries view
// Shows every incomplete statement for the logged-in user so they can see
// exactly which transactions still need a receipt uploaded.
// ─────────────────────────────────────────────────────────────────────────────

function StatementEntryRow({ entry }: { entry: MyStatementEntry }) {
  const dateLabel = entry.transactionDate ? tryParseDate(entry.transactionDate) : "—";
  const done = entry.matched || entry.noReceipt;
  return (
    <div className={`flex items-center justify-between py-3 px-4 border-b border-slate-100 last:border-b-0 transition-colors ${done ? "opacity-60" : "hover:bg-amber-50/40"}`}>
      <div className="flex items-center gap-3 min-w-0">
        {/* coloured dot: amber = needs receipt, green = done */}
        <div className={`w-2 h-2 rounded-full shrink-0 ${done ? "bg-emerald-400" : "bg-amber-400"}`} />
        <div className="min-w-0">
          <p className="font-medium text-slate-800 text-sm truncate" title={entry.description}>
            {entry.description}
          </p>
          <p className="text-xs text-slate-400">{dateLabel}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-3">
        <span className="font-semibold text-slate-800 text-sm tabular-nums">
          {formatCurrency(entry.amount, "GBP")}
        </span>
        {entry.matched && (
          <Badge variant="success" className="gap-1 hidden sm:flex">
            <CheckCheck className="w-3 h-3" /> Receipt
          </Badge>
        )}
        {!entry.matched && entry.noReceipt && (
          <Badge variant="secondary" className="gap-1 hidden sm:flex">
            <ShieldCheck className="w-3 h-3" /> No receipt
          </Badge>
        )}
        {!entry.matched && !entry.noReceipt && (
          <Badge variant="warning" className="gap-1 hidden sm:flex">
            <AlertCircle className="w-3 h-3" /> Needed
          </Badge>
        )}
      </div>
    </div>
  );
}

function UploadModal({
  uploadMutation,
  onClose,
  onUpload,
}: {
  uploadMutation: ReturnType<typeof useReceiptUpload>;
  onClose: () => void;
  onUpload: (files: File[]) => Promise<void>;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold">Upload Receipt</h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <FileDropzone
          onUpload={onUpload}
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          label="Drop receipt PDFs, images, or Word documents here"
          multiple={true}
        />
        {uploadMutation.isPending && (
          <p className="text-sm text-slate-500 mt-4 text-center">Uploading…</p>
        )}
        {uploadMutation.isSuccess && !uploadMutation.isPending && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2">
            <CheckCircle2 className="w-4 h-4" />
            Receipt uploaded successfully.
          </div>
        )}
        {uploadMutation.isError && (
          <p className="text-sm text-red-600 mt-4 text-center">
            Upload failed. Please try again or contact your administrator.
          </p>
        )}
      </div>
    </div>
  );
}

function UserStatementsView() {
  const { data: statements, isLoading, isError } = useMyStatements();
  const uploadMutation = useReceiptUpload();
  const queryClient = useQueryClient();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Record<number, boolean>>({});

  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      await uploadMutation.mutateAsync(file);
    }
    await queryClient.invalidateQueries({ queryKey: getMyStatementsQueryKey() });
    setIsUploadOpen(false);
  };

  const toggleStatement = (id: number) =>
    setCollapsedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const totalNeeded = useMemo(
    () => (statements ?? []).reduce(
      (sum, s) => sum + s.entries.filter((e) => !e.matched && !e.noReceipt).length,
      0,
    ),
    [statements],
  );

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* ── Header ── */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">My Receipts</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Check which transactions still need a receipt, then upload them below.
            </p>
          </div>
          <Button onClick={() => setIsUploadOpen(true)} className="shrink-0">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
        </div>

        {/* ── Summary banner ── */}
        {!isLoading && statements && (
          statements.length === 0 ? (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 mb-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800 text-sm">All statements up to date!</p>
                <p className="text-emerald-700 text-xs mt-0.5">
                  Every transaction on your statements has a receipt or has been marked accordingly.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-6">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">
                  {totalNeeded === 1
                    ? "1 transaction still needs a receipt"
                    : `${totalNeeded} transactions still need receipts`}
                </p>
                <p className="text-amber-700 text-xs mt-0.5">
                  Rows marked <span className="font-medium">Needed</span> are waiting for you to upload a receipt.
                </p>
              </div>
            </div>
          )
        )}

        {/* ── Loading ── */}
        {isLoading && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-12 text-center text-slate-400 text-sm">
            Loading your statements…
          </div>
        )}

        {/* ── Error ── */}
        {isError && !isLoading && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-4 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="font-semibold text-red-800 text-sm">Could not load your statements</p>
              <p className="text-red-700 text-xs mt-0.5">
                There was a problem fetching your data. Please refresh the page or contact your administrator.
              </p>
            </div>
          </div>
        )}

        {/* ── Statement cards ── */}
        {!isLoading && statements && statements.length > 0 && (
          <div className="space-y-3">
            {statements.map((stmt) => {
              const collapsed = collapsedIds[stmt.id] ?? false;
              const needed = stmt.entries.filter((e) => !e.matched && !e.noReceipt).length;
              const dateLabel = stmt.statementDate
                ? format(new Date(stmt.statementDate), "MMMM yyyy")
                : stmt.originalFilename;

              return (
                <div key={stmt.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleStatement(stmt.id)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {collapsed
                        ? <ChevronRight className="w-4 h-4 text-slate-400" />
                        : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="font-semibold text-slate-800 text-sm">{dateLabel}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs shrink-0">
                      <span className="text-slate-400">
                        {stmt.totalEntries} transaction{stmt.totalEntries !== 1 ? "s" : ""}
                      </span>
                      {needed > 0 ? (
                        <span className="font-medium text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                          {needed} needed
                        </span>
                      ) : (
                        <span className="font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                          Complete
                        </span>
                      )}
                    </div>
                  </button>

                  {!collapsed && (
                    <div className="border-t border-slate-100">
                      {stmt.entries.map((entry) => (
                        <StatementEntryRow key={entry.id} entry={entry} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Upload modal ── */}
      {isUploadOpen && (
        <UploadModal
          uploadMutation={uploadMutation}
          onClose={() => setIsUploadOpen(false)}
          onUpload={handleUpload}
        />
      )}
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin: full receipts management view (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export default function Receipts() {
  const [, navigate] = useLocation();
  const [filter, setFilter] = useState<"all" | "matched" | "unmatched" | "archived">("all");
  const listParams = filter === "archived"
    ? { archived: true }
    : { matched: filter === "all" ? undefined : filter === "matched" };
  const { user } = useAuth();
  const isAdmin = hasGroupAdmin(user, "expenses");

  const { data: receipts, isLoading } = useListReceipts(
    listParams,
    { query: { enabled: isAdmin } as any },
  );

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [collapsedMonths, setCollapsedMonths] = useState<Record<string, boolean>>({});
  const uploadMutation = useReceiptUpload();
  const reparseMutation = useReparseAllReceipts();
  const deleteMutation = useDeleteReceipt();
  const deleteAllMutation = useDeleteAllReceipts();

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("month");

  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      await uploadMutation.mutateAsync(file);
    }
    setIsUploadOpen(false);
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Delete this receipt?")) {
      deleteMutation.mutate(id);
    }
  };

  const filtered = useMemo(() => {
    if (!receipts) return [];
    const q = search.trim().toLowerCase();
    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(dateTo) : null;
    if (toDate) toDate.setHours(23, 59, 59, 999);

    return receipts.filter((r) => {
      if (q) {
        const hay = [r.vendor, r.originalFilename, r.invoiceNumber].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (fromDate || toDate) {
        const rd = parseReceiptDate(r.receiptDate);
        if (!rd) return false;
        if (fromDate && rd < fromDate) return false;
        if (toDate && rd > toDate) return false;
      }
      return true;
    });
  }, [receipts, search, dateFrom, dateTo]);

  const sortedFlat = useMemo(() => {
    if (sortMode === "month") return filtered;
    const arr = [...filtered];
    const dateOf = (r: Receipt) => parseReceiptDate(r.receiptDate)?.getTime() ?? 0;
    switch (sortMode) {
      case "date_desc": arr.sort((a, b) => dateOf(b) - dateOf(a)); break;
      case "date_asc": arr.sort((a, b) => dateOf(a) - dateOf(b)); break;
      case "amount_desc": arr.sort((a, b) => (b.amount ?? -Infinity) - (a.amount ?? -Infinity)); break;
      case "amount_asc": arr.sort((a, b) => (a.amount ?? Infinity) - (b.amount ?? Infinity)); break;
      case "vendor_asc": arr.sort((a, b) => (a.vendor || "zz").localeCompare(b.vendor || "zz")); break;
      case "uploaded_desc": arr.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()); break;
    }
    return arr;
  }, [filtered, sortMode]);

  const monthGroups: { key: string; label: string; receipts: Receipt[] }[] = [];
  if (sortMode === "month" && filtered.length > 0) {
    const groupMap: Record<string, { label: string; receipts: Receipt[] }> = {};
    for (const r of filtered) {
      const { key, label } = parseReceiptMonth(r.receiptDate);
      if (!groupMap[key]) groupMap[key] = { label, receipts: [] };
      groupMap[key].receipts.push(r);
    }
    const sortedKeys = Object.keys(groupMap).sort().reverse();
    for (const k of sortedKeys) {
      monthGroups.push({ key: k, label: groupMap[k].label, receipts: groupMap[k].receipts });
    }
  }

  const hasActiveFilters = search.trim() !== "" || dateFrom !== "" || dateTo !== "" || filter !== "all" || sortMode !== "month";
  const totalShown = sortMode === "month" ? filtered.length : sortedFlat.length;

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setFilter("all");
    setSortMode("month");
  };

  // Non-admin members of the expenses group see the outstanding receipts UI
  if (!isAdmin) {
    return <UserStatementsView />;
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Receipts</h1>
          <p className="text-slate-500 mt-1">Manage uploaded receipts and invoices.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && receipts && receipts.length > 0 && (
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => {
              if (confirm(`Delete all ${receipts.length} receipts? This cannot be undone.`)) {
                deleteAllMutation.mutate();
              }
            }} disabled={deleteAllMutation.isPending}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete All
            </Button>
          )}
          <Button variant="outline" onClick={() => reparseMutation.mutate()} disabled={reparseMutation.isPending}>
            <RefreshCw className={`w-4 h-4 mr-2 ${reparseMutation.isPending ? 'animate-spin' : ''}`} /> Re-parse All
          </Button>
          <Button onClick={() => setIsUploadOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Upload Receipt
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
        {/* Filter bar */}
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 space-y-3">
          {/* Row 1: search + sort */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vendor, filename or invoice number..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none bg-white"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600 whitespace-nowrap">Sort:</label>
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-primary/50 outline-none"
              >
                <option value="month">Group by month</option>
                <option value="date_desc">Receipt date (newest first)</option>
                <option value="date_asc">Receipt date (oldest first)</option>
                <option value="uploaded_desc">Recently uploaded</option>
                <option value="amount_desc">Amount (high to low)</option>
                <option value="amount_asc">Amount (low to high)</option>
                <option value="vendor_asc">Vendor (A–Z)</option>
              </select>
            </div>
          </div>

          {/* Row 2: status + date range + clear */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <Button variant={filter === "all" ? "default" : "ghost"} size="sm" onClick={() => setFilter("all")} className="rounded-full">All</Button>
              <Button variant={filter === "matched" ? "default" : "ghost"} size="sm" onClick={() => setFilter("matched")} className="rounded-full">Matched</Button>
              <Button variant={filter === "unmatched" ? "default" : "ghost"} size="sm" onClick={() => setFilter("unmatched")} className="rounded-full text-amber-600 hover:text-amber-700 hover:bg-amber-50">Unmatched</Button>
              <Button variant={filter === "archived" ? "default" : "ghost"} size="sm" onClick={() => setFilter("archived")} className="rounded-full text-muted-foreground">Archived</Button>
            </div>
            <div className="flex items-center gap-2 md:ml-auto">
              <label className="text-xs font-medium text-slate-600 whitespace-nowrap">Date from</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary/50 outline-none"
              />
              <label className="text-xs font-medium text-slate-600 whitespace-nowrap">to</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary/50 outline-none"
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-600">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Result summary */}
          {!isLoading && (
            <div className="text-xs text-slate-500">
              Showing {totalShown} of {receipts?.length ?? 0} receipts
              {hasActiveFilters && " (filtered)"}
            </div>
          )}
        </div>

        {isLoading && <div className="py-12 text-center text-slate-500">Loading receipts...</div>}

        {!isLoading && totalShown === 0 && (
          <div className="py-16 flex flex-col items-center justify-center text-slate-500 bg-slate-50 border-t border-slate-100">
            <ReceiptIcon className="w-12 h-12 text-slate-300 mb-3" />
            <p>{hasActiveFilters ? "No receipts match your filters." : "No receipts found."}</p>
          </div>
        )}

        {/* Month-grouped view */}
        {sortMode === "month" && monthGroups.map((group) => {
          const isCollapsed = collapsedMonths[group.key] !== false;
          const matchedInGroup = group.receipts.filter((r) => r.matched).length;
          const unmatchedInGroup = group.receipts.length - matchedInGroup;

          return (
            <div key={group.key}>
              <div
                className="px-6 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors flex items-center justify-between"
                onClick={() => setCollapsedMonths((prev) => ({ ...prev, [group.key]: !(prev[group.key] !== false) }))}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="font-semibold text-slate-800">{group.label}</span>
                  <span className="text-xs text-slate-500">
                    {group.receipts.length === 1 ? "1 receipt" : `${group.receipts.length} receipts`}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {matchedInGroup > 0 && <span className="text-green-600">{matchedInGroup} matched</span>}
                  {unmatchedInGroup > 0 && <span className="text-amber-600">{unmatchedInGroup} unmatched</span>}
                </div>
              </div>

              {!isCollapsed && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                  {group.receipts.map((receipt) => (
                    <ReceiptCard key={receipt.id} receipt={receipt} navigate={navigate} onDelete={handleDelete} deletePending={deleteMutation.isPending} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Flat sorted view */}
        {sortMode !== "month" && sortedFlat.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
            {sortedFlat.map((receipt) => (
              <ReceiptCard key={receipt.id} receipt={receipt} navigate={navigate} onDelete={handleDelete} deletePending={deleteMutation.isPending} />
            ))}
          </div>
        )}
      </div>

      {isUploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display font-bold">Upload Receipt</h2>
              <button onClick={() => setIsUploadOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <FileDropzone onUpload={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" label="Drop receipt PDFs, images, or Word documents here" multiple={true} />
          </div>
        </div>
      )}
    </Layout>
  );
}

function ReceiptCard({ receipt, navigate, onDelete, deletePending }: {
  receipt: Receipt & { uploadedByAdmin?: boolean; uploaderName?: string | null; statementInfo?: { id: number; originalFilename: string; statementDate: string | null } | null };
  navigate: (path: string) => void;
  onDelete: (id: number, e: React.MouseEvent) => void;
  deletePending: boolean;
}) {
  return (
    <div onClick={() => navigate(`/receipts/${receipt.id}`)} className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow relative group bg-white cursor-pointer">
      <div className="absolute top-4 right-4 flex items-center gap-1">
        {receipt.uploadedByAdmin && (
          <Badge variant="secondary" title={`Uploaded by admin${receipt.uploaderName ? `: ${receipt.uploaderName}` : ""}`} className="gap-1">
            <ShieldCheck className="w-3 h-3" />
            Admin
          </Badge>
        )}
        {receipt.archived
          ? <Badge variant="secondary" title={receipt.archivedReason ?? undefined}>Archived</Badge>
          : receipt.matched ? <Badge variant="success">Matched</Badge> : <Badge variant="warning">Unmatched</Badge>}
        <button
          onClick={(e) => onDelete(receipt.id, e)}
          disabled={deletePending}
          className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 border border-red-200 disabled:opacity-50"
          title="Delete receipt"
          data-testid={`button-delete-receipt-${receipt.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
        <ReceiptIcon className="w-6 h-6 text-primary" />
      </div>

      <h3 className="font-semibold text-slate-900 text-lg mb-1 truncate" title={receipt.vendor || "Unknown Vendor"}>
        {receipt.vendor || "Unknown Vendor"}
      </h3>
      <p className="text-xs text-slate-400 truncate mb-3" title={receipt.originalFilename}>
        {receipt.originalFilename}
      </p>

      <div className="space-y-2 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          {receipt.receiptDate ? tryParseDate(receipt.receiptDate) : 'Date not found'}
        </div>
        {receipt.invoiceNumber && (
          <div className="text-xs text-slate-400">Invoice: {receipt.invoiceNumber}</div>
        )}
        {receipt.statementInfo && (
          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-2 py-1">
            <FileText className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate" title={receipt.statementInfo.originalFilename}>
              {receipt.statementInfo.originalFilename}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <span className="font-semibold text-slate-900 text-base">
            {receipt.amount ? formatCurrency(receipt.amount, receipt.currency || 'GBP') : 'Amount missing'}
          </span>
          <span className="text-xs text-primary font-medium">Click to review</span>
        </div>
      </div>
    </div>
  );
}
